<template>
  <div class="header-container drag">
    <div class="header-panel">
      <div class="header-status">
        <div class="back-indicator" aria-hidden="true" @click="backToSelector">
          <NavIcon name="back" />
        </div>
        <div class="device-meta">
          <div class="title-row">
            <span class="status-dot" :class="{ connected: isConnected }"></span>
            <div class="title-main">
              <span v-if="currentDevice" class="device-title">{{ $t(currentDevice.name) }}</span>
            </div>
          </div>
          <div class="device-subtitle">
            <span class="subtitle-label">{{ $t('connected_status') }}</span>
            <span class="subtitle-divider"></span>
            <span class="fw-tag">{{ $t('firmware_version') }} {{ fwVersion }}</span>
            <template v-if="firmware.isUpgrade.value">
              <span class="subtitle-divider upgrade-divider"></span>
              <button class="firmware-update-notice" @click.stop="setBox(2)">
                <span class="notice-ping"></span>
                <svg class="notice-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2v7M5 6l3 3 3-3M3 12h10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span class="notice-text">{{ $t('upgrade_des') }}</span>
                <svg class="notice-arrow" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </template>
            <template v-if="isShowPower">
              <span class="subtitle-divider"></span>
              <div class="subtitle-battery">
                <Power class="header-power" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="header-controls">
        <div class="quick-actions">
          <button class="appearance-entry theme-entry" :title="$t('software_theme')" @click="openSoftwareTheme">
            <span class="entry-icon" aria-hidden="true">
              <SvgAsset :content="softwareThemeSvg" />
            </span>
            <span class="entry-text">{{ $t('software_theme') }}</span>
          </button>

          <button class="appearance-entry" :title="$t('virtual_device_style')" @click="openKeyboardAppearance">
            <span class="entry-icon" aria-hidden="true">
              <SvgAsset :content="keyboardAppearanceSvg" />
            </span>
            <span class="entry-text">{{ $t('virtual_device_style') }}</span>
          </button>

          <button class="icon-btn" :title="$t('frequently_asked_questions')" @click="toQuestions">
            <NavIcon name="faq" />
            <span class="tip_bubble">{{ $t('frequently_asked_questions') }}</span>
          </button>

          <button class="icon-btn" :title="$t('online_manual') || 'Online manual'" @click="manualEvent">
            <NavIcon name="manual" />
            <span class="tip_bubble">{{ $t('online_manual') || 'Online manual' }}</span>
          </button>

          <button class="icon-btn" :title="$t('feedback_text') || 'Feedback'" @click="setfeedback">
            <NavIcon name="feedback" />
            <span class="tip_bubble">{{ $t('feedback_text') || 'Feedback' }}</span>
          </button>

          <button class="icon-btn" :title="$t('set') || 'Settings'" @click.stop="setBox(3)">
            <NavIcon name="set" />
            <span class="tip_bubble">{{ $t('set') || 'Settings' }}</span>
          </button>
        </div>

        <div class="utility-panel">
          <div class="language" v-click-outside="closeLang">
            <button class="lang-trigger" @click="toggleLang">
              <span class="lang-code">{{ currentLangCode }}</span>
              <span class="lang-label">{{ currentLangName }}</span>
              <span class="arrow" :class="{ active: isShowLangList }"></span>
            </button>

            <transition name="fade">
              <div class="lang-list" v-if="isShowLangList">
                <div class="lang-item" v-for="(item, index) in languages" :key="index" :class="{ active: selectValue == index }" @click="changeLang(index)">
                  {{ item.name }}
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { actionSet as settingActionSet, SetEnum, setBoxBool } from '../setting/setting.vue'
import { selectValue } from '../../model/interaction'
import { feedbackBoxBool } from '../feedback/feedback.vue'
import { languages, Language, updateLanguage } from '../../languages/i18n'
import firmware from '@/upgrade/firmware'
import SvgAsset from '../common/SvgAsset.vue'
import keyboardAppearanceSvg from '@/assets/inline-svg/keyboard-appearance.svg?raw'
import softwareThemeSvg from '@/assets/inline-svg/software-theme.svg?raw'
import { Brand, brand } from '@/brand/brand'
import { openExternalLink } from '@/tool/utility'
import { getCurrentDevice } from '@/profiles/selector'
import { DeviceManager } from '@/service/device'
import { DevicePid } from '@/define/define'
import { speciaBehiver, specialCheck } from '@/define/limit'
import { operate } from '@/service/driver/operate'
import { actionSet as driverActionSet } from '@/service/driver/action_set'
import { openKeyboardAppearancePanel } from '@/service/keyboard_appearance'
import { openSoftwareThemePanel } from '@/service/software_theme'
import { closeAllAppearancePanels } from '@/service/appearance_panel_registry'
import NavIcon from './nav_icon.vue'
import Power from '../power/power.vue'

const isShowLangList = ref(false)
const fwVersion = ref('--')
const currentPid = computed(() => DeviceManager.instance.state.currentPid)
const currentDevice = computed(() => getCurrentDevice.value)
const isConnected = computed(() => currentPid.value !== DevicePid.None)
const isShowPower = computed(() => {
  return specialCheck(speciaBehiver.ShowBattery, currentPid.value) && DeviceManager.instance.state.deviceStatus.IsConnect
})

const currentLangName = computed(() => {
  if (selectValue.value === -1) return 'Languages'
  return languages[selectValue.value]?.name || 'Languages'
})

const currentLangCode = computed(() => {
  if (selectValue.value === -1) return 'GL'
  const lang = languages[selectValue.value]?.value
  if (typeof lang !== 'string') return 'GL'
  return lang.slice(0, 2).toUpperCase()
})

const updateFirmwareVersion = async () => {
  if (!isConnected.value) {
    fwVersion.value = '--'
    return
  }

  try {
    fwVersion.value = await firmware.getFirmwareVersion()
  } catch (e) {
    fwVersion.value = '--'
  }
}

const toggleLang = () => {
  isShowLangList.value = !isShowLangList.value
}

const closeLang = () => {
  isShowLangList.value = false
}

const changeLang = (index: number) => {
  selectValue.value = index
  isShowLangList.value = false
}

const vClickOutside = {
  mounted(el: any, binding: any) {
    el.clickOutsideEvent = (event: any) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value()
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: any) {
    document.removeEventListener('click', el.clickOutsideEvent)
  },
}

const setBox = (n: number) => {
  closeAllAppearancePanels()
  setBoxBool.value = true
  settingActionSet.value = n
}

const openKeyboardAppearance = () => {
  openKeyboardAppearancePanel()
}

const openSoftwareTheme = () => {
  openSoftwareThemePanel()
}

const setfeedback = () => {
  feedbackBoxBool.value = true
}

const backToSelector = () => {
  if (!isConnected.value) return
  DeviceManager.instance.resetState()
  operate.currentKey.value = -1
  driverActionSet.resetData()
}

watch(selectValue, (newVal) => {
  const lang: Language = languages[newVal].value as Language
  updateLanguage(lang)
})

watch(
  currentPid,
  () => {
    updateFirmwareVersion()
  },
  { immediate: true },
)

onMounted(() => {
  updateFirmwareVersion()
})

const toQuestions = () => {
  if (brand.value === Brand.irok) {
    openExternalLink('https://www.irok.cn/help/0')
  } else if (brand.value === Brand.iyx) {
    openExternalLink('https://www.iyx.fun/help/0')
  } else if (brand.value === Brand.carotmas) {
    openExternalLink('https://www.carotmas.com/help/0')
  }
}

const openManual = () => {
  if (brand.value === Brand.irok) {
    openExternalLink('https://manual.irok.cn/view/device')
  } else if (brand.value === Brand.iyx) {
    openExternalLink('https://manual.iyx.fun/view/device')
  } else if (brand.value === Brand.carotmas) {
    openExternalLink('https://manual.carotmas.com/view/device')
  }
}

const manualEvent = () => openManual()
</script>

<style scoped lang="less">
.header-container {
  display: flex;
  align-items: center;
  height: 84px;
  width: 100%;
  padding: 0 16px;
  position: relative;
  z-index: 100;
  flex-shrink: 0;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.08); /* 顶部 Header: 背景 */
  backdrop-filter: blur(25px); /* 毛玻璃特效 */

  .header-panel {
    width: 100%;
    height: 100%;
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 0 4px;
    background: transparent;
    border: none;
    backdrop-filter: none;
    box-shadow: none;
  }

  .header-status {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    min-width: 0;
    transition: var(--transition-base);

    .back-indicator {
      -webkit-app-region: no-drag;
      cursor: pointer;
      width: 34px;
      height: 34px;
      border-radius: var(--radius-s);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: var(--header-back-bg);
      border: 1px solid var(--header-back-border);
      color: var(--sidebar-text-muted);
      transition: var(--transition-base);

      .svg_asset,
      svg {
        width: 16px;
        height: 16px;
        transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
      }
    }

    &:hover {
      .back-indicator {
        background: var(--header-back-hover-bg);
        border-color: rgba(var(--color-brand-1-rgb), 0.3);
        color: var(--surface-text-primary);

        .svg_asset,
        svg {
          transform: translateX(-2px);
        }
      }

      .device-meta {
        .title-row {
          .device-title {
            color: var(--shell-text-primary);
          }

          .status-dot {
            box-shadow: 0 0 0 6px rgba(var(--color-brand-1-rgb), 0.08);

            &.connected {
              box-shadow:
                0 0 0 6px rgba(74, 222, 128, 0.12),
                0 0 16px rgba(74, 222, 128, 0.45),
                0 0 0 10px rgba(var(--color-brand-1-rgb), 0.08);
            }
          }
        }

        .device-subtitle {
          color: var(--shell-text-secondary);
        }
      }
    }

    .brand-mark {
      width: 120px;
      height: 40px;
      background-size: contain;
      background-position: left center;
      background-repeat: no-repeat;
      flex-shrink: 0;
    }

    .device-meta {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 4px;
      min-width: 0;

      .title-row {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
        width: 100%;

        .title-main {
          display: inline-flex;
          align-items: center;
          min-width: 0;
          flex: 1;
        }
      }

      .status-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.4);
        box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.06);
        flex-shrink: 0;

        &.connected {
          background: #4ade80;
          box-shadow:
            0 0 0 6px rgba(74, 222, 128, 0.12),
            0 0 16px rgba(74, 222, 128, 0.45);
          animation: pulseDot 2s ease-in-out infinite;
        }
      }

      .device-title {
        flex: 0 1 auto;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--sidebar-text);
        font-size: 20px;
        font-weight: 800;
        font-style: italic;
        letter-spacing: -0.02em;
      }

      .device-subtitle {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        color: var(--sidebar-text-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        overflow: visible;

        .subtitle-label,
        .fw-tag,
        .subtitle-battery {
          flex-shrink: 0;
        }

        .subtitle-divider {
          width: 1px;
          height: 12px;
          background: var(--shell-divider);
          flex-shrink: 0;
        }

        .upgrade-divider {
          background: rgba(255, 164, 40, 0.4);
        }

        .fw-tag {
          font-family: var(--font-mono);
          letter-spacing: 0.08em;
        }

        .firmware-update-notice {
          position: relative;
          height: 26px;
          padding: 0 10px 0 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid rgba(255, 176, 64, 0.4);
          border-radius: var(--radius-pill);
          background: linear-gradient(135deg, rgba(255, 168, 0, 0.22) 0%, rgba(255, 94, 58, 0.2) 100%);
          color: #fff1d6;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: none;
          white-space: nowrap;
          cursor: pointer;
          overflow: hidden;
          -webkit-app-region: no-drag;
          transition: var(--transition-base);
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.05),
            0 0 0 0 rgba(255, 168, 0, 0.32),
            0 8px 20px rgba(255, 94, 58, 0.16);
          animation: firmwareNoticePulse 2.4s ease-in-out infinite;

          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -65%;
            width: 52%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            animation: firmwareNoticeSweep 2.4s ease-in-out infinite;
            pointer-events: none;
          }

          .notice-ping,
          .notice-icon,
          .notice-text,
          .notice-arrow {
            position: relative;
            z-index: 1;
          }

          .notice-ping {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #ffd37a;
            box-shadow: 0 0 0 0 rgba(255, 211, 122, 0.55);
            animation: firmwareNoticeDot 1.8s ease-in-out infinite;
            flex-shrink: 0;
          }

          .notice-icon,
          .notice-arrow {
            width: 12px;
            height: 12px;
            flex-shrink: 0;
          }

          .notice-arrow {
            opacity: 0.8;
            transition:
              transform 0.3s ease,
              opacity 0.3s ease;
          }

          &:hover {
            transform: translateY(-2px);
            border-color: rgba(255, 196, 92, 0.65);
            color: #ffffff;
            box-shadow:
              inset 0 0 0 1px rgba(255, 255, 255, 0.08),
              0 0 0 4px rgba(255, 168, 0, 0.12),
              0 12px 26px rgba(255, 94, 58, 0.24);
            animation: none;

            &::before {
              animation: none;
              left: 120%;
            }

            .notice-arrow {
              transform: translateX(2px);
              opacity: 1;
            }
          }
        }

        .subtitle-battery {
          display: inline-flex;
          align-items: center;
          min-width: 0;
          letter-spacing: normal;
          text-transform: none;
          --power-text-color: var(--sidebar-text);
          --power-empty-text-color: #ff5757;
          --power-fill-color: var(--color-white);
          --power-empty-fill-color: #ff5757;
          --power-message-text-color: var(--surface-text-secondary);
          --power-message-bg: var(--color-white);

          :deep(.device_power) {
            --power-icon-width: 40px;
            --power-icon-height: 20px;
            --power-fill-width: 28px;
            --power-fill-height: 12px;
            --power-fill-left: 5px;
            --power-low-battery-left: 16px;
            gap: 4px;
          }

          :deep(.power_icon) {
            width: var(--power-icon-width);
            height: var(--power-icon-height);
          }

          :deep(.power_bg) {
            height: var(--power-fill-height);
            left: var(--power-fill-left);
          }

          :deep(.low_battery) {
            left: var(--power-low-battery-left);
            transform: scale(0.9);
            transform-origin: center;
          }

          :deep(.power_num) {
            min-width: 30px;
            font-size: 11px;
            letter-spacing: 0;
          }
        }
      }
    }
  }

  .header-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    min-width: 0;
    overflow: visible;
    padding: 8px 0;

    .quick-actions,
    .utility-panel {
      display: flex;
      align-items: center;
      gap: 8px;
      background: var(--header-toolbar-bg);
      padding: 4px;
      border-radius: 12px;
      border: 1px solid var(--header-toolbar-border);
    }

    .utility-panel {
      gap: 10px;
      padding-right: 4px;
    }

    .icon-btn {
      width: 38px;
      height: 38px;
      cursor: pointer;
      position: relative;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid transparent;
      color: var(--sidebar-text-muted);
      -webkit-app-region: no-drag;
      background: transparent;

      &:hover {
        background-color: var(--header-icon-hover-bg);
        border-color: var(--header-icon-hover-border);
        transform: translateY(-2px);
        color: var(--shell-text-primary);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);

        .tip_bubble {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
        }
      }

      .svg_asset,
      svg {
        width: 16px;
        height: 16px;
        stroke: currentColor;
        stroke-width: 2.5;
        transition: transform 0.3s ease;
      }

      &:hover .svg_asset,
      &:hover svg {
        transform: scale(1.1);
      }

      &.compact {
        width: 34px;
        height: 34px;
      }

      &.close-btn:hover {
        background: rgba(255, 87, 87, 0.14);
        color: #d33b2f;
      }

      .tip_bubble {
        position: absolute;
        top: calc(100% + 10px);
        left: 50%;
        transform: translateX(-50%) translateY(10px);
        background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(10px);
        color: #fff;
        padding: 7px 10px;
        border-radius: var(--radius-s);
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
        z-index: 1000;

        &::after {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-bottom-color: rgba(0, 0, 0, 0.85);
        }
      }
    }

    .appearance-entry {
      height: 38px;
      padding: 0 14px;
      cursor: pointer;
      position: relative;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border: 1px solid rgba(var(--color-brand-1-rgb), 0.18);
      color: var(--shell-text-primary);
      -webkit-app-region: no-drag;
      background: var(--header-entry-bg);
      white-space: nowrap;

      &:hover {
        background: var(--header-entry-hover-bg);
        border-color: rgba(var(--color-brand-1-rgb), 0.3);
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);

        .entry-icon {
          color: var(--brand-color-pure);
          transform: scale(1.08);
        }
        .entry-text {
          color: var(--brand-color-pure);
        }
      }

      .entry-icon {
        width: 16px;
        height: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--shell-text-secondary);
        transition: var(--transition-base);

        :deep(.svg_asset),
        svg {
          width: 16px;
          height: 16px;
        }
      }

      .entry-text {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.02em;
      }
    }
  }
}

.language {
  position: relative;
  -webkit-app-region: no-drag;

  .lang-trigger {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    min-width: 144px;
    height: 38px;
    padding: 0 12px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 12px;
    color: var(--sidebar-text);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: var(--transition-base);
    -webkit-app-region: no-drag;

    &:hover {
      background: var(--header-icon-hover-bg);
      border-color: var(--header-toolbar-border);
      transform: none;
    }

    .lang-code {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: var(--brand-color);
      color: #ffffff;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.08em;
      flex-shrink: 0;
    }

    .lang-label {
      max-width: 70px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--sidebar-text);
    }

    .arrow {
      display: inline-block;
      width: 7px;
      height: 7px;
      border-right: 1.5px solid currentColor;
      border-bottom: 1.5px solid currentColor;
      transform: translateY(-2px) rotate(45deg);
      margin-left: auto;
      transition: var(--transition-base);

      &.active {
        transform: translateY(2px) rotate(-135deg);
      }
    }
  }

  .lang-list {
    position: absolute;
    top: calc(100% + 10px);
    left: 0;
    min-width: 100%;
    background: var(--header-dropdown-bg);
    backdrop-filter: blur(28px);
    border: 1px solid var(--header-dropdown-border);
    border-radius: var(--radius-s);
    padding: 6px;
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.12);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 2px;
    animation: slideIn 0.3s cubic-bezier(0.23, 1, 0.32, 1);

    .lang-item {
      padding: 8px 12px;
      font-size: 12px;
      color: var(--surface-text-primary);
      border-radius: var(--radius-s);
      cursor: pointer;
      transition: var(--transition-base);
      white-space: nowrap;

      &:hover {
        background: rgba(var(--color-brand-1-rgb), 0.1);
        color: var(--brand-color-pure);
      }

      &.active {
        background: var(--brand-color);
        color: #fff;
      }
    }
  }
}

@keyframes firmwareNoticePulse {
  0%,
  100% {
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.05),
      0 0 0 0 rgba(255, 168, 0, 0.32),
      0 8px 20px rgba(255, 94, 58, 0.16);
  }

  50% {
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.08),
      0 0 0 5px rgba(255, 168, 0, 0),
      0 10px 26px rgba(255, 94, 58, 0.24);
  }
}

@keyframes firmwareNoticeSweep {
  0% {
    left: -65%;
  }

  55%,
  100% {
    left: 115%;
  }
}

@keyframes firmwareNoticeDot {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 211, 122, 0.55);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 0 0 5px rgba(255, 211, 122, 0);
    transform: scale(1.08);
  }
}

@keyframes pulseDot {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.08);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 1540px) {
  .header-container {
    .header-panel {
      gap: 12px;
      padding: 0 14px;
    }

    .header-status {
      .brand-mark {
        width: 96px;
      }

      .device-meta {
        .device-title {
          max-width: 260px;
          font-size: 18px;
        }

        .device-subtitle {
          letter-spacing: 0.08em;
          gap: 6px;
        }
      }
    }

    .header-controls {
      .quick-actions {
        gap: 4px;

        .appearance-entry {
          padding: 0 12px;

          .entry-text {
            max-width: 92px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      .language .lang-trigger {
        min-width: 120px;
      }
    }
  }
}
</style>
